import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import SidebarLayout from "./components/layout/SidebarLayout";
import { ProjectsProvider } from "./context/ProjectsContext";
import RoleGuard from "./components/RoleGuard";

const MapPage = React.lazy(() => import("./components/pages/MapPage"));
// REMOVED: CscSitesPage
const SavedIECFormsPage = React.lazy(() =>
  import("./components/pages/SavedIECFormsPage")
);
const DriveHistoryPage = React.lazy(() =>
  import("./components/pages/DriveHistoryPage")
);
const UnregisteredWorkersPage = React.lazy(() =>
  import("./components/pages/UnregisteredWorkersPage")
);
const AnimatedHeatMap = React.lazy(
  () => import("./components/AnimatedHeatMap") // adjust path if needed
);
const AssetPublicView = React.lazy(() =>
  import("./components/pages/AssetPublicView")
);
const DriveRoute = React.lazy(() => import("./components/pages/DriveRoute"));
const QRAssetsPage = React.lazy(() =>
  import("./components/pages/QRAssetsPage")
);
const QRAssetsList = React.lazy(() =>
  import("./components/pages/QRAssetsList")
);
const QRMetaAdmin = React.lazy(() => import("./components/pages/QRMetaAdmin"));
const AdminCreateCenterHead = React.lazy(() =>
  import("./components/pages/AdminCreateDivisionalManager")
);
const CenterHeadCreateDriveUser = React.lazy(() =>
  import("./components/pages/DivisionalManagerCreateSupervisor")
);
const ManageUsers = React.lazy(() => import("./components/pages/ManageUsers"));
const TrackKaroPage = React.lazy(() =>
  import("./components/pages/TrackKaroPage")
);
const CsvPdfUtilityPage = React.lazy(() =>
  import("./components/pages/CsvPdfUtilityPage")
);

function Protected({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("kskfield_user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const onLogin = (u) => {
    setUser(u);
    localStorage.setItem("kskfield_user", JSON.stringify(u));
  };

  const onLogout = () => {
    setUser(null);
    localStorage.removeItem("kskfield_user");
    localStorage.removeItem("kskfield_auth");
  };

  return (
    <BrowserRouter>
      <ProjectsProvider>
        <React.Suspense
          fallback={<div style={{ padding: 16 }}>Loading...</div>}
        >
          <Routes>
            <Route path="/login" element={<LoginPage onLogin={onLogin} />} />

            {/* Public QR routes (adjust guards as per your policy) */}
            {/* Public QR routes (no role guard) */}
            <Route path="/qrassets" element={<QRAssetsPage />} />
            <Route path="/qrassets/list" element={<QRAssetsList />} />
            <Route path="/qrassets/meta" element={<QRMetaAdmin />} />

            {/* Main app */}
            <Route path="/a/:qrId" element={<AssetPublicView />} />
            <Route
              path="/dashboard"
              element={
                <Protected user={user}>
                  <SidebarLayout onLogout={onLogout} />
                </Protected>
              }
            >
              <Route
                path="users/new-center-head"
                element={
                  <RoleGuard roles={["ADMIN"]}>
                    <AdminCreateCenterHead />
                  </RoleGuard>
                }
              />

              <Route
                path="users/new-drive-user"
                element={
                  <RoleGuard roles={["DIVISIONAL_MANAGER"]}>
                    <CenterHeadCreateDriveUser />
                  </RoleGuard>
                }
              />
              <Route
                path="users/manage"
                element={
                  <RoleGuard roles={["ADMIN", "DIVISIONAL_MANAGER"]}>
                    <ManageUsers />
                  </RoleGuard>
                }
              />

              <Route index element={<Navigate to="map" replace />} />

              {/* Map available to all logged-in roles */}
              <Route path="map" element={<MapPage />} />

              <Route
                path="trackkaro"
                element={
                  <RoleGuard roles={["ADMIN", "DIVISIONAL_MANAGER"]}>
                    <TrackKaroPage />
                  </RoleGuard>
                }
              />

              {/* View saved IEC (managers only) */}
              <Route
                path="saved-iec"
                element={
                  <RoleGuard
                    roles={["ADMIN", "DIVISIONAL_MANAGER", "SUPERVISOR"]}
                  >
                    <SavedIECFormsPage />
                  </RoleGuard>
                }
              />

              {/* Drive history (managers only) */}
              <Route
                path="drive-history"
                element={
                  <RoleGuard
                    roles={["ADMIN", "DIVISIONAL_MANAGER", "SUPERVISOR"]}
                  >
                    <DriveHistoryPage />
                  </RoleGuard>
                }
              />

              {/* Drive route / Unregistered workers create-submit (all roles incl. SUPERVISOR) */}
              <Route
                path="drive/:projectId"
                element={
                  <RoleGuard
                    roles={["ADMIN", "DIVISIONAL_MANAGER", "SUPERVISOR"]}
                  >
                    <DriveRoute />
                  </RoleGuard>
                }
              />
              <Route
                path="unregisteredworkers/:projectId"
                element={
                  <RoleGuard
                    roles={["ADMIN", "DIVISIONAL_MANAGER", "SUPERVISOR"]}
                  >
                    <UnregisteredWorkersPage />
                  </RoleGuard>
                }
              />
            </Route>

            {/* Heatmap (usually managers) */}
            <Route
              path="/dashboard/heatmap"
              element={
                <RoleGuard roles={["ADMIN", "DIVISIONAL_MANAGER"]}>
                  <AnimatedHeatMap />
                </RoleGuard>
              }
            />

            <Route
              path="/circlereports"
              element={<CsvPdfUtilityPage />}
            />

            {/* Fallback */}
            <Route
              path="*"
              element={<Navigate to={user ? "/dashboard" : "/login"} replace />}
            />
          </Routes>
        </React.Suspense>
      </ProjectsProvider>
    </BrowserRouter>
  );
}
